/*
 * Size advice in the size guide behind the Maattabel button
 * (snippets/product-size-advice.liquid, inside snippets/jc-size-guide.liquid).
 *
 * Who + height + shoulders + belly -> one size, its flat measurements and a
 * "Kies maat L" button that selects it. Nothing is preselected and nothing
 * is marked on the size buttons (Thomas 08-10): the customer always makes
 * the choice, in the guide or on the buttons.
 *
 * Where the rule comes from (PDP-#50, #57):
 * - The models on the photos: women of 1.64 to 1.78 m wear S; men of 1.82 to
 *   1.86 m wear M or L (metafield custom.model_maat_handleiding, checked on
 *   photo 3 for 37 sweaters on 08-10-2026).
 * - Thomas (08-10-2026): 1.83 m, not muscular, wears L (shoulders and sleeve
 *   length); a very slim man of 1.80 m or more needs M. So the height gives
 *   the base, the shoulders and the belly move it one size either way.
 * - Bol returns season 2025: M comes back as "too small" twice as often as
 *   "too big" (117 vs 60); XS and S come back as "too big" more often; the
 *   children's sizes run large. A sweater with lights runs a little small
 *   (Thomas 08-10): fit "smaller" shifts the height bands down 2 cm.
 * - The factory sheet (snippets/jc-size-table.liquid) gives the measurements
 *   shown with the advice: chest, length, sleeve and shoulder, flat, in cm.
 *
 * Children's sizes are heights (98-104, 110-116, 146/152/158): the range the
 * child's height falls in, taken from the sizes this product has.
 */
if (!customElements.get('size-advice')) {
  class SizeAdvice extends HTMLElement {
    constructor() {
      super();
      // Upper bound of the height (cm) for each size, per person.
      this.tables = {
        woman: [[159, 'XS'], [176, 'S'], [184, 'M'], [192, 'L'], [999, 'XL']],
        man: [[169, 'S'], [177, 'M'], [185, 'L'], [192, 'XL'], [999, '2XL']],
      };
      this.order = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL', '6XL'];
      this.shift = { narrow: -1, normal: 0, broad: 1, slim: -1, athletic: 0, round: 1 };
      this.state = this.restore();
      this.sizeTable = this.readTable();
    }

    connectedCallback() {
      this.whoButtons = [...this.querySelectorAll('[data-who]')];
      this.inputs = this.querySelector('[data-inputs]');
      this.heightSelect = this.querySelector('[data-height]');
      this.shapes = this.querySelector('[data-shapes]');
      this.shoulderButtons = [...this.querySelectorAll('[data-shoulders]')];
      this.bellyButtons = [...this.querySelectorAll('[data-belly]')];
      this.result = this.querySelector('[data-result]');
      this.resultText = this.querySelector('[data-result-text]');
      this.resultNote = this.querySelector('[data-result-note]');
      this.choose = this.querySelector('[data-choose]');
      this.measures = this.querySelector('[data-measures]');
      // The guide opens in a modal outside the buy column: find the form on the page.
      this.form = this.closest('.product-info') || document.querySelector('.product-info') || document;

      this.whoButtons.forEach((button) => button.addEventListener('click', () => this.chooseWho(button.dataset.who)));
      this.heightSelect.addEventListener('change', () => this.update());
      this.shoulderButtons.forEach((button) => button.addEventListener('click', () => this.pick('shoulders', button)));
      this.bellyButtons.forEach((button) => button.addEventListener('click', () => this.pick('belly', button)));
      this.choose.addEventListener('click', () => this.selectSize());

      if (this.state.shoulders) this.activate(this.shoulderButtons, 'shoulders', this.state.shoulders);
      if (this.state.belly) this.activate(this.bellyButtons, 'belly', this.state.belly);
      if (this.state.who && this.whoButtons.some((b) => b.dataset.who === this.state.who)) {
        this.chooseWho(this.state.who, true);
      }
    }

    readTable() {
      try {
        return JSON.parse(document.getElementById('jc-size-table').textContent);
      } catch (error) {
        return null;
      }
    }

    chooseWho(who, silent) {
      this.state.who = who;
      this.whoButtons.forEach((button) => button.classList.toggle('is-active', button.dataset.who === who));
      this.fillHeights(who);
      this.inputs.hidden = false;
      this.shapes.hidden = who === 'kid';
      if (!silent) this.heightSelect.focus();
      this.update();
    }

    pick(kind, button) {
      this.state[kind] = button.dataset[kind];
      this.activate(kind === 'shoulders' ? this.shoulderButtons : this.bellyButtons, kind, this.state[kind]);
      this.update();
    }

    activate(buttons, kind, value) {
      buttons.forEach((button) => button.classList.toggle('is-active', button.dataset[kind] === value));
    }

    fillHeights(who) {
      const kid = who === 'kid';
      // 1 cm steps for adults: the bands turn at 177 and 185, and a man of 1.83 m
      // must be able to say 1.83 (Thomas). Children's sizes are 6 cm wide: 2 cm steps.
      const [from, to, step] = kid ? [92, 170, 2] : [150, 205, 1];
      const current = Number(kid ? this.state.kidHeight : this.state.height) || (kid ? 122 : 175);
      this.heightSelect.innerHTML = '';
      for (let cm = from; cm <= to; cm += step) {
        const option = document.createElement('option');
        option.value = String(cm);
        option.textContent = kid
          ? this.dataset.textCm.replace('[height]', String(cm))
          : this.dataset.textMetres.replace('[height]', `${Math.floor(cm / 100)},${String(cm % 100).padStart(2, '0')} m`);
        if (cm === current) option.selected = true;
        this.heightSelect.appendChild(option);
      }
    }

    adviceFor(who, height) {
      if (who === 'kid') return this.kidSize(height);
      // A sweater that runs a little small: judge the height as if 2 cm taller,
      // so only people near a band's edge move up (Thomas 08-10: "iets kleiner").
      const judged = this.dataset.fit === 'smaller' ? height + 2 : height;
      const table = this.tables[who];
      let index = this.order.indexOf(table.find(([max]) => judged <= max)[1]);
      index += this.shift[this.state.shoulders || 'normal'] + this.shift[this.state.belly || 'athletic'];
      // Shoulders and belly together move at most one size.
      const base = this.order.indexOf(table.find(([max]) => judged <= max)[1]);
      index = Math.max(base - 1, Math.min(base + 1, index));
      index = Math.max(0, Math.min(this.order.length - 1, index));
      return this.order[index];
    }

    /**
     * The product's own children's size whose range holds the height. On the
     * size page there is no product: then the children's sizes of the factory
     * table (#jc-size-table), which are the shop's children's sizes.
     */
    kidSize(height) {
      const own = (this.dataset.kidsValues || '').split('|').filter(Boolean);
      const values = own.length ? own : Object.keys(this.sizeTable?.kind || {});
      const ranges = values.map((value) => {
        const numbers = value.split(/[^0-9]+/).filter(Boolean).map(Number);
        return { value, min: Math.min(...numbers), max: Math.max(...numbers) };
      }).sort((a, b) => a.min - b.min);
      if (!ranges.length) return null;
      const hit = ranges.find((range) => height <= range.max && height >= range.min - 5);
      if (hit) return hit.value;
      return height < ranges[0].min ? ranges[0].value : ranges[ranges.length - 1].value;
    }

    update() {
      const who = this.state.who;
      if (!who) return;
      const height = Number(this.heightSelect.value);
      if (who === 'kid') this.state.kidHeight = height;
      else this.state.height = height;
      this.persist();

      const size = this.adviceFor(who, height);
      if (!size) return;
      this.advised = size;
      const input = this.sizeInput(size);
      const soldOut = input ? input.disabled || input.classList.contains('is-unavailable') : false;
      const label = input ? this.form.querySelector(`label[for="${input.id}"]`) : null;
      const shown = label ? label.textContent.trim() : size;
      this.resultText.textContent = this.dataset.textResult.replace('[size]', shown) + (soldOut ? ` ${this.dataset.textSoldOut}` : '');
      this.resultNote.textContent = who === 'kid' ? this.dataset.textKidsNote : '';
      this.choose.textContent = this.dataset.textChoose.replace('[size]', shown);
      this.choose.hidden = !input || soldOut || input.checked;
      this.renderMeasures(who, size, shown);
      this.result.hidden = false;
    }

    /** "L, plat gemeten: borst 56 · lengte 73 · mouw 67 · schouder 45,5 cm" from the factory sheet. */
    renderMeasures(who, size, shown) {
      const group = this.sizeTable ? this.sizeTable[who === 'kid' ? 'kind' : 'volwassen'] : null;
      const row = group ? group[size] : null;
      if (!row || row.borst === null || row.borst === undefined) {
        this.measures.hidden = true;
        return;
      }
      const cm = (value) => String(value).replace('.0', '').replace('.', ',');
      this.measures.textContent = this.dataset.textMeasures
        .replace('[size]', shown)
        .replace('[chest]', cm(row.borst))
        .replace('[length]', cm(row.lengte))
        .replace('[sleeve]', cm(row.mouw))
        .replace('[shoulder]', cm(row.schouder));
      this.measures.hidden = false;
    }

    /** "Kies maat L": the customer's own tap selects the size, the guide closes, the buy button is next. */
    selectSize() {
      const input = this.sizeInput(this.advised);
      if (!input) return;
      const label = this.form.querySelector(`label[for="${input.id}"]`);
      if (label) label.click();
      else input.click();
      this.choose.hidden = true;
      const modal = this.closest('modal-dialog');
      if (modal && typeof modal.close === 'function') modal.close();
      const button = this.form.querySelector('button[name="add"]');
      if (button) button.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    sizeInput(size) {
      return [...this.form.querySelectorAll('input.opt-btn')].find((input) => input.value === size) || null;
    }

    restore() {
      try {
        return JSON.parse(localStorage.getItem('jc-size-advice') || '{}') || {};
      } catch (error) {
        return {};
      }
    }

    persist() {
      try {
        localStorage.setItem('jc-size-advice', JSON.stringify(this.state));
      } catch (error) {
        // Private window or blocked storage: the advice still works for this page.
      }
    }
  }

  customElements.define('size-advice', SizeAdvice);
}
